import React, { useState, useEffect, useRef } from "react";
import Swal from "sweetalert2";
import Modal from "react-modal";
import CustomSelect from "./CustomSelect";
import Field, { inputStyle } from "./Field";
import RichTextToolbar from "./RichTextToolbar";
import RichTextEditor from "./RichTextEditor";
import useCategoryOptions from "../hooks/useCategoryOptions.jsx";
import {
  customStyles,
  variantInputStyle,
  sectionTitleStyle,
  fieldsetStyle,
  unidadesSAT,
  generalFields,
  priceFields,
  DEFAULT_PRODUCT,
  useDiscountConfig,
  tagStyles,
} from "../hooks/useAddProductConfig";

const AddProductModal = ({ isOpen, onClose, onSubmit, editingProduct }) => {
  const closeRef = useRef(null);
  const editorRef = useRef(null);
  const [product, setProduct] = useState(DEFAULT_PRODUCT);
  const { discountFields } = useDiscountConfig(product, setProduct);
  const [previews, setPreviews] = useState([null]);
  const [variantValues, setVariantValues] = useState([""]);
  const [variantTypes, setVariantTypes] = useState([""]);
  const [contextMenu, setContextMenu] = useState({
    visible: false,
    x: 0,
    y: 0,
    idx: null,
  });
  const [hoveredOption, setHoveredOption] = useState(null);
  const [activeTab, setActiveTab] = useState("descripcion");
  const categoryOptions = useCategoryOptions(
    product?.categoria ?? "",
    "categorias"
  );

  // Mueve el cursor al final del contenido
  const setCaretToEnd = (el) => {
    if (!el) return;
    const range = document.createRange();
    range.selectNodeContents(el);
    range.collapse(false);
    const sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(range);
  };

  // Captura pulsaciones imprimibles y las inserta al final
  const handleKeyDown = (e) => {
    const k = e.key.toLowerCase();
    if ((e.ctrlKey || e.metaKey) && ["b", "i", "u"].includes(k)) {
      e.preventDefault();
      const cmd = k === "b" ? "bold" : k === "i" ? "italic" : "underline";
      document.execCommand(cmd, false, null);
      setProduct((pr) => ({ ...pr, [activeTab]: editorRef.current.innerHTML }));
    }
    // Si no es atajo -> dejar que el navegador escriba donde esté la selección
  };

  // Carga data al abrir o cambiar producto
  useEffect(() => {
    if (!isOpen) return;

    const base = editingProduct
      ? {
          ...DEFAULT_PRODUCT,
          ...editingProduct,
          descuento: "",
          tipoDescuento: "",
        }
      : DEFAULT_PRODUCT;

    setProduct(base);
    setPreviews(() => {
      const arr = new Array(5).fill(null);
      const imgs = Array.isArray(base.imagenes)
        ? base.imagenes.filter(Boolean)
        : [];

      // Asegurar que la principal vaya primero
      if (base.imagen) {
        if (!imgs.length || imgs[0] !== base.imagen) imgs.unshift(base.imagen);
      }

      for (let i = 0; i < Math.min(5, imgs.length); i++) arr[i] = imgs[i];
      return arr;
    });

    if (base.variantes) {
      const tipos = Object.keys(base.variantes);
      const valores = tipos.map((t) => base.variantes[t].join(", "));
      setVariantTypes(tipos);
      setVariantValues(valores);
    } else {
      setVariantTypes([""]);
      setVariantValues([""]);
    }
  }, [isOpen, editingProduct]);

  // Ejecuta comando de toolbar y mueve cursor
  const exec = (command, value = null) => {
    editorRef.current?.focus();
    document.execCommand(command, false, value);
    setProduct((pr) => ({ ...pr, [activeTab]: editorRef.current.innerHTML }));
  };

  // Al cambiar contenido
  const handleEditorInput = () => {
    setProduct((pr) => ({
      ...pr,
      [activeTab]: editorRef.current.innerHTML,
    }));
  };
  // cambia el tipo en la posición idx
  const handleVariantTypeChange = (idx, value) => {
    setVariantTypes((ts) => {
      const copy = [...ts];
      copy[idx] = value;
      return copy;
    });
  };

  // Cambios en inputs normales e imágenes
  const handleChange = (e) => {
    const { name, value, files } = e.target;

    // Imágenes (slots imagen-0 ... imagen-4)
    if (name.startsWith("imagen-")) {
      const idx = Number(name.split("-")[1]);
      const file = files && files[0];
      if (!file || !file.type?.startsWith("image/")) return;

      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = reader.result;

        // Actualiza el preview del slot correspondiente
        setPreviews((p) => {
          const copy = [...p];
          copy[idx] = dataUrl;
          return copy;
        });

        // Solo escribir product.imagen si es el slot principal (0)
        if (idx === 0) {
          setProduct((pr) => ({ ...pr, imagen: dataUrl }));
        }
      };
      reader.readAsDataURL(file);
      return;
    }

    // Etiquetas
    if (name === "etiqueta") {
      const tag = (value || "").trim();
      if (tag) {
        setProduct((pr) => ({ ...pr, etiquetas: [...pr.etiquetas, tag] }));
      }
      e.target.value = "";
      return;
    }

    // Resto de inputs
    setProduct((pr) => ({ ...pr, [name]: value }));
  };

  // Elimina etiqueta
  const removeTag = (tag) => {
    setProduct((pr) => ({
      ...pr,
      etiquetas: pr.etiquetas.filter((t) => t !== tag),
    }));
  };

  // cambia un valor en la posición idx
  const handleVariantChange = (idx, value) => {
    setVariantValues((vs) => {
      const copy = [...vs];
      copy[idx] = value;
      return copy;
    });
  };

  // añade un nuevo input vacío
  const addVariant = () => {
    setVariantValues((vs) => [...vs, ""]);
  };

  // quita la variante en la posición idx
  const handleRemoveVariant = (idx) => {
    setVariantValues((vs) => vs.filter((_, i) => i !== idx));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Solo obligatorios: nombre y precio
    const nombreOk = !!(product.nombre && product.nombre.trim());
    const costoNum = Number(product.costo);
    const costoOk = !Number.isNaN(costoNum) && costoNum > 0;

    if (!nombreOk || !costoOk) {
      Swal.fire({
        icon: "warning",
        title: "Faltan datos",
        text:
          !nombreOk && !costoOk
            ? "Completa el Nombre y el Precio."
            : !nombreOk
            ? "Completa el Nombre."
            : "Ingresa un Precio válido (mayor a 0).",
        confirmButtonText: "Entendido",
        confirmButtonColor: "#0081FF",
      });
      return;
    }

    // Construir objeto de variantes a partir de los inputs
    const variantesObj = variantTypes.reduce((obj, tipo, idx) => {
      const t = (tipo || "").trim();
      if (t) {
        obj[t] = (variantValues[idx] || "")
          .split(",")
          .map((v) => v.trim())
          .filter(Boolean);
      }
      return obj;
    }, {});

    // Enviar al padre (él guarda en Firestore y muestra el éxito)
    await onSubmit({
      ...product,
      costo: costoNum, // normalizado a número
      categoria: (product.categoria || "").trim(),
      variantes: variantesObj,
      imagen: previews[0] || null, // principal
      imagenes: previews.filter(Boolean), // todas las que existan
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      style={customStyles}
      ariaHideApp={false}
      onAfterOpen={() => {
        closeRef.current?.focus();
        setTimeout(() => setCaretToEnd(editorRef.current), 0);
      }}
    >
      {/* Encabezado + Botones */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          margin: "10px 0 16px", // sin margen izquierda/derecha
        }}
      >
        <h2 style={{ margin: 0 }}>
          {editingProduct ? "Editar Producto" : "Agregar producto"}
        </h2>

        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: "#EF4444",
              border: "none",
              color: "#FFF",
              borderRadius: 6,
              padding: "12px",
              cursor: "pointer",
              fontWeight: "bold",
            }}
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            style={{
              background: "#0081FF",
              border: "none",
              color: "#FFF",
              borderRadius: 6,
              padding: "12px",
              fontWeight: "bold",
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              cursor: "pointer",
            }}
          >
            <i className="bi bi-plus-lg" />
            Guardar
          </button>
        </div>
      </div>

      <form
        onSubmit={handleSubmit}
        style={{ display: "flex", height: "calc(100% - 64px)" }}
      >
        {/* Columna izquierda */}
        <div style={{ flex: 2, overflowY: "auto", paddingRight: 32 }}>
          {/* Información General */}
          <fieldset style={fieldsetStyle}>
            <h3
              style={{ ...sectionTitleStyle, marginTop: 0, marginBottom: 16 }}
            >
              Información General
            </h3>
            {/* Nombre */}
            <div style={{ marginBottom: 16 }}>
              <label
                style={{ display: "block", marginBottom: 12, color: "red" }}
              >
                Nombre*
              </label>
              <input
                name="nombre"
                value={product.nombre}
                onChange={handleChange}
                placeholder="Introduce Nombre"
                style={inputStyle}
              />
            </div>
            {/* Modelo / Número de Parte */}
            <div style={{ display: "flex", gap: 24, marginBottom: 24 }}>
              {generalFields.map((f) =>
                f.name === "categoria" ? (
                  <Field
                    key="categoria"
                    label={f.label || "Categoría"}
                    name="categoria"
                    isSelect
                    options={categoryOptions}
                    value={product.categoria}
                    onChange={handleChange}
                    placeholder="Seleccionar categoría"
                  />
                ) : (
                  <Field
                    key={f.name}
                    {...f}
                    value={product[f.name]}
                    onChange={f.onChange || handleChange}
                  />
                )
              )}
            </div>

            {/* Pestañas Descripción / Especificaciones / Notas */}
            <div style={{ marginBottom: 16 }}>
              {/* encabezado de pestañas */}
              <div
                style={{
                  display: "flex",
                  borderBottom: "1px solid #E6EDFF",
                  marginBottom: 12,
                }}
              >
                {[
                  { key: "descripcion", label: "Descripción" },
                  { key: "especificaciones", label: "Especificaciones" },
                  { key: "notas", label: "Notas" },
                ].map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => setActiveTab(tab.key)}
                    style={{
                      background: "none",
                      border: "none",
                      borderBottom:
                        activeTab === tab.key ? "2px solid #0081FF" : "none",
                      padding: "8px 16px",
                      cursor: "pointer",
                      fontWeight: activeTab === tab.key ? 600 : 400,
                      color: activeTab === tab.key ? "#111" : "#6B7280",
                    }}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* área de editor */}
              <div
                style={{
                  border: "1px solid #E6EDFF",
                  borderRadius: 4,
                  overflow: "hidden",
                }}
              >
                <RichTextToolbar exec={exec} />
                <RichTextEditor
                  ref={editorRef}
                  html={product[activeTab]}
                  onKeyDown={handleKeyDown}
                  onInput={handleEditorInput}
                  style={{ minHeight: 100, padding: 12 }}
                  placeholder={
                    activeTab === "descripcion"
                      ? "Introducir Descripción"
                      : activeTab === "especificaciones"
                      ? "Introducir Especificaciones"
                      : "Introducir Notas"
                  }
                />
              </div>
            </div>
            {/* Variantes */}
            <div style={{ marginBottom: 24 }}>
              <label style={{ display: "block", marginBottom: 12 }}>
                Variantes:
              </label>

              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {variantValues.map((val, idx) => (
                  <div
                    key={idx}
                    style={{ display: "flex", alignItems: "center", gap: 20 }}
                  >
                    {/* selector con ancho fijo un poco más ancho */}
                    <CustomSelect
                      name={`varianteTipo-${idx}`}
                      value={variantTypes[idx] || ""}
                      onChange={(e) =>
                        handleVariantTypeChange(idx, e.target.value)
                      }
                      options={[
                        { value: "Largo", label: "Largo" },
                        { value: "Color", label: "Color" },
                        // si necesitas más tipos, añádelos aquí
                      ]}
                      placeholder="Seleccionar tipo"
                      style={{
                        flex: "0 0 280px",
                        minWidth: 160,
                      }}
                    />

                    {/* input que ocupa el resto, ahora con mínimo más amplio */}
                    <input
                      type="text"
                      placeholder="Introducir valores separados por una coma"
                      value={val}
                      onChange={(e) => handleVariantChange(idx, e.target.value)}
                      style={{
                        flex: 1, // toma todo el espacio restante
                        minWidth: 320, // mínimo 350px
                        ...variantInputStyle,
                      }}
                    />

                    {idx > 0 && (
                      <button
                        type="button"
                        onClick={() => handleRemoveVariant(idx)}
                        style={{
                          width: 26,
                          height: 26,
                          background: "#F2F2F2",
                          border: "none",
                          borderRadius: 8,
                          color: "#0070FF",
                          fontSize: 18,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: "pointer",
                        }}
                      >
                        −
                      </button>
                    )}
                  </div>
                ))}
              </div>

              <button
                type="button"
                onClick={addVariant}
                style={{
                  marginTop: 12,
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  background: "#0081FF",
                  color: "#FFF",
                  border: "none",
                  borderRadius: 6,
                  padding: "8px 16px",
                  fontSize: 14,
                  cursor: "pointer",
                }}
              >
                <span style={{ fontSize: 18, lineHeight: 1 }}>+</span>
                Agregar Variante
              </button>
            </div>

            {/* Unidad SAT & Clave SAT */}
            <div style={{ display: "flex", gap: 24, marginBottom: 24 }}>
              <div style={{ flex: 1 }}>
                <label style={{ display: "block", marginBottom: 12 }}>
                  {" "}
                  Unidad de Medida SAT:{" "}
                </label>
                <CustomSelect
                  name="unidadSAT"
                  value={product.unidadSAT}
                  onChange={handleChange}
                  placeholder="Seleccionar Unidad"
                  options={unidadesSAT.map((u) => ({
                    value: u.clave,
                    label: `${u.clave} — ${u.nombre}`,
                  }))}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ display: "block", marginBottom: 12 }}>
                  {" "}
                  Clave de Producto SAT:{" "}
                </label>
                <input
                  name="claveSAT"
                  value={product.claveSAT}
                  onChange={handleChange}
                  placeholder="Clave Producto SAT"
                  style={inputStyle}
                />
              </div>
            </div>
          </fieldset>

          {/* Precio y Almacén */}
          <fieldset style={fieldsetStyle}>
            <h3
              style={{ ...sectionTitleStyle, marginTop: 0, marginBottom: 16 }}
            >
              {" "}
              Precio y Almacén{" "}
            </h3>
            <div style={{ display: "flex", gap: 24, marginBottom: 24 }}>
              {priceFields.map((f) => (
                <Field
                  key={f.name}
                  {...f}
                  value={product[f.name]}
                  onChange={handleChange}
                />
              ))}
            </div>

            {/* SOLO TUS DOS CUSTOM SELECT */}
            <div style={{ display: "flex", gap: 24, marginBottom: 24 }}>
              {discountFields.map((f) => (
                <div key={f.name} style={{ flex: 1 }}>
                  <label style={{ display: "block", marginBottom: 12 }}>
                    {" "}
                    {f.label}:{" "}
                  </label>
                  <CustomSelect
                    name={f.name}
                    value={f.value}
                    onChange={f.onChange}
                    placeholder={f.placeholder}
                    options={f.options}
                  />
                </div>
              ))}
            </div>

            {/* CAMPO ALMACÉN POR SEPARADO */}
            <div style={{ marginBottom: 24 }}>
              <Field
                label="Almacén"
                name="almacen"
                value={product.almacen}
                onChange={handleChange}
                placeholder="Nombre de almacén"
              />
            </div>
          </fieldset>
        </div>

        {/* Columna derecha */}
        <div
          style={{
            flex: 1,
            paddingLeft: 32,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Imágenes */}
          <fieldset
            style={{
              ...fieldsetStyle,
              flex: 1,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <h3
              style={{ ...sectionTitleStyle, marginTop: 0, marginBottom: 16 }}
            >
              Imágenes
            </h3>
            <label
              htmlFor="imagen-0"
              style={{
                position: "relative",
                flex: "0 0 300px", // altura fija
                height: 300, // fallback
                border: "2px dashed #E0E5F1",
                borderRadius: 6,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                marginBottom: 12,
                background: "#FAFAFB",
                overflow: "hidden", // evita que la imagen sobresalga
              }}
            >
              {previews[0] ? (
                <>
                  <img
                    src={previews[0]}
                    alt=""
                    style={{
                      position: "absolute",
                      inset: 0,
                      width: "100%",
                      height: "100%",
                      objectFit: "contain", // usa "cover" si prefieres recorte
                      display: "block",
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setPreviews((p) => {
                        const copy = [...p];
                        copy[0] = null;
                        return copy;
                      });
                      setProduct((pr) => ({ ...pr, imagen: null }));
                    }}
                    style={{
                      position: "absolute",
                      top: "6px",
                      right: "6px", // dentro del contenedor (no se corta)
                      width: 20,
                      height: 20,
                      padding: 0,
                      border: "none",
                      borderRadius: "50%",
                      background: "rgba(0,0,0,0.6)",
                      color: "#fff",
                      fontSize: 14,
                      lineHeight: 1,
                      cursor: "pointer",
                    }}
                    aria-label="Eliminar imagen principal"
                  >
                    ×
                  </button>
                </>
              ) : (
                <span style={{ color: "#999" }}>Imagen Principal</span>
              )}
            </label>

            <input
              id="imagen-0"
              name="imagen-0"
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              onChange={handleChange}
            />
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(4,1fr)",
                gap: 8,
              }}
            >
              {[1, 2, 3, 4].map((i) => (
                <React.Fragment key={i}>
                  <label
                    htmlFor={`imagen-${i}`}
                    onContextMenu={(e) => {
                      e.preventDefault();
                      setContextMenu({
                        visible: true,
                        x: e.clientX,
                        y: e.clientY,
                        idx: i,
                      });
                    }}
                    style={{
                      border: "1px dashed #E0E5F1",
                      borderRadius: 4,
                      height: 60,
                      position: "relative",
                      overflow: "hidden",
                      cursor: "pointer",
                      background: "#FAFAFB",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {previews[i] ? (
                      <img
                        src={previews[i]}
                        alt={`Vista previa ${i}`}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      <span style={{ color: "#CCC" }}>+</span>
                    )}
                    {previews[i] && (
                      <button
                        type="button"
                        onClick={() => {
                          setPreviews((p) => {
                            const copy = [...p];
                            copy[i] = null;
                            return copy;
                          });
                        }}
                        style={{
                          position: "absolute",
                          top: 4,
                          right: -6,
                          width: 20,
                          height: 20,
                          padding: 0,
                          border: "none",
                          borderRadius: "50%",
                          background: "rgba(0,0,0,0.6)",
                          color: "#fff",
                          fontSize: 14,
                          lineHeight: 1,
                          cursor: "pointer",
                        }}
                        aria-label="Eliminar imagen"
                      >
                        ×
                      </button>
                    )}
                  </label>
                  <input
                    id={`imagen-${i}`}
                    name={`imagen-${i}`}
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={handleChange}
                  />
                </React.Fragment>
              ))}
            </div>
            {contextMenu.visible && (
              <ul
                style={{
                  position: "fixed",
                  top: contextMenu.y,
                  left: contextMenu.x,
                  background: "#FFF",
                  border: "1px solid #CCC",
                  borderRadius: 6,
                  listStyle: "none",
                  margin: 0,
                  padding: 0,
                  zIndex: 3000,
                }}
                onMouseLeave={() =>
                  setContextMenu((c) => ({ ...c, visible: false }))
                }
              >
                <li
                  onMouseEnter={() => setHoveredOption("setMain")}
                  onMouseLeave={() => setHoveredOption(null)}
                  onClick={() => {
                    setPreviews((p) => {
                      const copy = [...p];
                      const newMain = copy[contextMenu.idx];
                      [copy[0], copy[contextMenu.idx]] = [
                        copy[contextMenu.idx],
                        copy[0],
                      ];
                      // Mantener product.imagen en sync
                      setProduct((pr) => ({ ...pr, imagen: newMain || null }));
                      return copy;
                    });
                    setContextMenu((c) => ({ ...c, visible: false }));
                  }}
                  style={{
                    padding: "8px 12px",
                    cursor: "pointer",
                    background:
                      hoveredOption === "setMain" ? "#F5F5F5" : "transparent",
                    boxShadow:
                      hoveredOption === "setMain"
                        ? "0 2px 8px rgba(0,0,0,0.15)"
                        : "none",
                    transition: "box-shadow 0.2s, background 0.2s",
                  }}
                >
                  Establecer como imagen principal
                </li>
                <li
                  onMouseEnter={() => setHoveredOption("delete")}
                  onMouseLeave={() => setHoveredOption(null)}
                  onClick={() => {
                    setPreviews((p) => {
                      const copy = [...p];
                      copy.splice(contextMenu.idx, 1);
                      copy.push(null);
                      return copy;
                    });
                    setContextMenu((c) => ({ ...c, visible: false }));
                  }}
                  style={{
                    padding: "8px 12px",
                    cursor: "pointer",
                    color: "red",
                    background:
                      hoveredOption === "delete" ? "#FFEDEC" : "transparent",
                    boxShadow:
                      hoveredOption === "delete"
                        ? "0 2px 8px rgba(0,0,0,0.15)"
                        : "none",
                    transition: "box-shadow 0.2s, background 0.2s",
                  }}
                >
                  Eliminar
                </li>
              </ul>
            )}
          </fieldset>

          {/* Etiquetas */}
          <fieldset style={fieldsetStyle}>
            <h3
              style={{ ...sectionTitleStyle, marginTop: 0, marginBottom: 16 }}
            >
              Etiquetas
            </h3>

            <div style={tagStyles.tagsBox}>
              {product.etiquetas.map((tag, i) => (
                <div key={`${tag}-${i}`} style={tagStyles.tagChip(i)}>
                  {tag}
                  <span
                    style={tagStyles.tagClose}
                    onClick={() => removeTag(tag)}
                    aria-label={`Eliminar etiqueta ${tag}`}
                  >
                    <i className="bi bi-x"></i>
                  </span>
                </div>
              ))}

              <input
                name="etiqueta"
                placeholder="Añadir etiqueta y Enter..."
                onKeyDown={(e) => {
                  if (["Enter"].includes(e.key)) {
                    e.preventDefault();
                    handleChange(e); // tu handler ya agrega y limpia
                  }
                  // opcional: borrar último chip con Backspace si input vacío
                  if (e.key === "Backspace" && !e.currentTarget.value) {
                    setProduct((pr) => ({
                      ...pr,
                      etiquetas: pr.etiquetas.slice(0, -1),
                    }));
                  }
                }}
                style={tagStyles.tagInput}
              />
            </div>
          </fieldset>
        </div>
      </form>
    </Modal>
  );
};

export default AddProductModal;
